<template>
    <div class="itc-page">

        <!-- PAGE HEADER -->
        <!-- <div class="itc-header">
            <div>
                <h1 class="itc-title">Types d'interventions</h1>
                <p class="itc-subtitle">
                    Sélectionnez une catégorie pour gérer ses types d'intervention.<br />
                    Catégorisez et standardisez les interventions de votre entreprise.
                </p>
            </div>
        </div> -->

        <!-- LOADER -->
        <div v-if="loading" class="itc-loader-wrap">
            <div class="flex flex-col items-center justify-center py-24">
                <div class="flex gap-2 mb-4">
                    <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce"></div>
                </div>
                <p class="text-sm text-gray-500">Chargement des types d'interventions…</p>
            </div>
        </div>

        <!-- LAYOUT -->
        <div v-else class="itc-layout">
            <!-- SIDEBAR FOLDERS -->
            <div class="itc-sidebar">
                <div class="itc-sidebar-header">Catégories</div>
                <div class="itc-folder-list">
                    <div
                        v-for="cat in categories"
                        :key="cat.key"
                        class="itc-folder-item"
                        :class="{ active: activeCategory === cat.key }"
                        @click="selectCategory(cat.key)">
                        <img
                            v-if="cat.icon"
                            :src="cat.icon"
                            class="itc-folder-icon"
                            alt="" />
                        <span class="itc-folder-label">{{ cat.label }}</span>
                        <span class="itc-folder-count">{{ countForCategory(cat.key) }}</span>
                    </div>
                </div>
            </div>

            <!-- MAIN CONTENT -->
            <div class="itc-main">

                <!-- NO CATEGORY SELECTED -->
                <div v-if="!activeCategory" class="itc-no-select">
                    <div class="itc-no-select-icon">
                        <i class="fas fa-folder-open"></i>
                    </div>
                    <p>Sélectionnez une catégorie dans la liste<br />pour gérer ses types d'intervention</p>
                </div>

                <template v-else>

                    <!-- TOOLBAR -->
                    <div class="itc-toolbar">
                        <div class="itc-search-wrap">
                            <i class="fas fa-search itc-search-icon"></i>
                            <input
                                v-model="filters.search"
                                type="text"
                                class="itc-search-input"
                                placeholder="Rechercher un type..." />
                        </div>

                        <select v-model="filters.type_logique" class="itc-filter-select">
                            <option value="">Tous les types</option>
                            <option
                                v-for="t in typesLogiques"
                                :key="t.value"
                                :value="t.value">
                                {{ t.label }}
                            </option>
                        </select>

                        <button class="itc-btn-primary" @click="openCreate">
                            <i class="fas fa-plus"></i>
                            Nouveau type
                        </button>
                    </div>

                    <!-- EMPTY STATE -->
                    <div v-if="filteredTypes.length === 0" class="itc-empty">
                        <div class="itc-empty-icon">
                            <img
                                v-if="activeCategoryObj && activeCategoryObj.icon"
                                :src="activeCategoryObj.icon"
                                style="width:40px;height:40px;opacity:0.2" />
                            <i v-else class="fas fa-inbox"></i>
                        </div>
                        <strong>{{ isFiltered ? 'Aucun résultat' : 'Aucun type dans cette catégorie' }}</strong>
                        <p>{{ isFiltered ? 'Essayez de modifier vos filtres.' : 'Créez le premier type d\'intervention pour ' + activeCategoryObj.label + '.' }}</p>
                    </div>

                    <!-- TABLE -->
                    <div v-else class="itc-table-wrap">
                        <table class="itc-table">
                            <thead>
                                <tr>
                                    <th style="width:40%">Nom</th>
                                    <th style="width:25%">Type logique</th>
                                    <th style="width:15%;text-align:right">Durée (min)</th>
                                    <th style="width:20%;text-align:right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in filteredTypes" :key="row.id" class="itc-row">
                                    <!-- NOM - inline edit -->
                                    <td>
                                        <input
                                            class="itc-cell-input"
                                            :value="row.nom"
                                            @blur="onNomBlur(row, $event)"
                                            @keydown.enter="$event.target.blur()" />
                                    </td>

                                    <!-- TYPE LOGIQUE - inline select -->
                                    <td>
                                        <select
                                            class="itc-cell-select"
                                            :value="row.type_logique"
                                            @change="onTypeLogiqueChange(row, $event)">
                                            <option
                                                v-for="t in typesLogiques"
                                                :key="t.value"
                                                :value="t.value">
                                                {{ t.label }}
                                            </option>
                                        </select>
                                    </td>

                                    <!-- DURÉE - inline edit -->
                                    <td>
                                        <div class="itc-duration-cell">
                                            <input
                                                type="number"
                                                min="0"
                                                class="itc-duration-input"
                                                :value="row.time_min"
                                                @blur="onDureeBlur(row, $event)"
                                                @keydown.enter="$event.target.blur()" />
                                            <span class="itc-duration-unit">min</span>
                                        </div>
                                    </td>

                                    <!-- ACTIONS -->
                                    <td>
                                        <div class="itc-action-cell">
                                            <button class="itc-btn-icon" @click="openEdit(row)" title="Modifier">
                                                <i class="fas fa-pen"></i>
                                            </button>
                                            <button class="itc-btn-icon itc-btn-danger" @click="remove(row)" title="Supprimer">
                                                <i class="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                </template>
            </div>
        </div>

        <!-- MODAL -->
        <modal name="type-modal" height="auto" :scrollable="true" :adaptive="true">
            <div class="itc-modal-inner">

                <!-- MODAL HEADER -->
                <div class="itc-modal-header">
                    <div>
                        <h2 class="itc-modal-title">
                            {{ mode === 'create' ? 'Créer un type d\'intervention' : 'Modifier le type' }}
                        </h2>
                        <div v-if="activeCategoryObj" class="itc-modal-cat-tag">
                            <img
                                v-if="activeCategoryObj.icon"
                                :src="activeCategoryObj.icon"
                                class="itc-modal-cat-icon" />
                            {{ activeCategoryObj.label }}
                        </div>
                    </div>
                    <button class="itc-btn-close" @click="$modal.hide('type-modal')">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <!-- MODAL BODY -->
                <div class="itc-modal-body">

                    <div class="itc-form-group">
                        <label class="itc-form-label">Nom</label>
                        <input
                            v-model="form.nom"
                            type="text"
                            class="itc-form-input"
                            placeholder="Ex : Entretien annuel chaudière" />
                    </div>

                    <div class="itc-form-row">
                        <div class="itc-form-group">
                            <label class="itc-form-label">Type logique</label>
                            <select v-model="form.type_logique" class="itc-form-select">
                                <option value="" disabled>Sélectionner…</option>
                                <option
                                    v-for="t in typesLogiques"
                                    :key="t.value"
                                    :value="t.value">
                                    {{ t.label }}
                                </option>
                            </select>
                        </div>

                        <div class="itc-form-group">
                            <label class="itc-form-label">Durée par défaut (min)</label>
                            <input
                                v-model="form.time_min"
                                type="number"
                                min="0"
                                class="itc-form-input"
                                placeholder="60" />
                        </div>
                    </div>

                    <div class="itc-form-group">
                        <label class="itc-form-label">Description</label>
                        <textarea
                            v-model="form.description"
                            rows="3"
                            class="itc-form-textarea"
                            placeholder="Description optionnelle…">
                        </textarea>
                    </div>

                </div>

                <!-- MODAL FOOTER -->
                <div class="itc-modal-footer">
                    <button class="itc-btn-secondary" @click="$modal.hide('type-modal')">
                        Annuler
                    </button>
                    <button class="itc-btn-primary" @click="save">
                        <i class="fas fa-check"></i>
                        Enregistrer
                    </button>
                </div>

            </div>
        </modal>

    </div>
</template>

<script>
import TypeLogiqueIntervention from '@/config/TypeLogiqueIntervention.js'

export default {
    name: 'InterventionTypesCrud',

    data() {
        return {
            types: [],
            categories: [],
            typesLogiques: TypeLogiqueIntervention,

            activeCategory: null,

            mode: 'create',
            form: this.emptyForm(),

            filters: {
                search: '',
                type_logique: '',
            },

            loading: true,
        }
    },

    computed: {
        activeCategoryObj() {
            if (!this.activeCategory) return null
            return this.categories.find(c => c.key === this.activeCategory) || null
        },

        isFiltered() {
            return !!(this.filters.search || this.filters.type_logique)
        },

        filteredTypes() {
            const search = this.filters.search.toLowerCase()
            const logique = this.filters.type_logique

            return this.types.filter(t => {
                if (t.categorie !== this.activeCategory) return false
                if (search && !t.nom.toLowerCase().includes(search)) return false
                if (logique && t.type_logique !== logique) return false
                return true
            })
        },
    },

    methods: {
        emptyForm() {
            return {
                id: null,
                nom: '',
                categorie: '',
                type_logique: '',
                description: '',
                time_min: '',
                societe_id: this.$societeId,
            }
        },

        countForCategory(key) {
            return this.types.filter(t => t.categorie === key).length
        },

        selectCategory(key) {
            this.activeCategory = key
            this.filters.search = ''
            this.filters.type_logique = ''
        },

        async load() {
            this.loading = true
            this.types = await this.$request.post('intervention-types/list', {
                societe_id: this.$societeId,
            })

            this.categories = await this.$request.post('/reference/get-categories', {})
            this.loading = false
        },

        openCreate() {
            this.mode = 'create'
            this.form = {
                ...this.emptyForm(),
                categorie: this.activeCategory,
            }
            this.$modal.show('type-modal')
        },

        openEdit(type) {
            this.mode = 'update'
            this.form = JSON.parse(JSON.stringify(type))
            this.$modal.show('type-modal')
        },

        async save() {
            if (!this.form.nom || !this.form.nom.trim()) return

            const route = this.mode === 'create'
                ? 'intervention-types/create'
                : 'intervention-types/update'

            await this.$request.post(route, this.form)
            this.$toast.success('Type enregistré')
            this.$modal.hide('type-modal')
            this.load()
        },

        async remove(type) {
            if (!confirm(`Supprimer « ${type.nom} » ?`)) return

            await this.$request.post('intervention-types/delete', {
                id: type.id,
                societe_id: type.societe_id,
            })

            this.$toast.success('Type supprimé')
            this.load()
        },

        async updateCell(id, field, value) {
            await this.$request.post('intervention-types/update-cell', {
                id,
                field,
                value,
                societe_id: this.$societeId,
            })
        },

        onNomBlur(row, event) {
            const val = event.target.value.trim()
            if (val && val !== row.nom) {
                row.nom = val
                this.updateCell(row.id, 'nom', val)
            }
        },

        onTypeLogiqueChange(row, event) {
            const val = event.target.value
            row.type_logique = val
            this.updateCell(row.id, 'type_logique', val)
        },

        onDureeBlur(row, event) {
            const val = parseInt(event.target.value) || 0
            if (val !== row.time_min) {
                row.time_min = val
                this.updateCell(row.id, 'time_min', val)
            }
        },
    },

    mounted() {
        this.load()
    },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

.itc-page {
    font-family: 'DM Sans', sans-serif;
    padding: 32px;
    min-height: 100vh;
    background: #F5F4F1;
    color: #1A1916;
}

/* ── HEADER ──────────────────────────────────── */
.itc-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 28px;
}
.itc-title {
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.3px;
    color: #1A1916;
    margin: 0;
}
.itc-subtitle {
    font-size: 13.5px;
    color: #6B6860;
    font-weight: 400;
    margin-top: 4px;
    line-height: 1.6;
}

/* ── LAYOUT ──────────────────────────────────── */
.itc-layout {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 20px;
    align-items: start;
}

/* ── SIDEBAR ─────────────────────────────────── */
.itc-sidebar {
    background: #ffffff;
    border: 1px solid #E4E2DC;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    position: sticky;
    top: 20px;
}
.itc-sidebar-header {
    padding: 13px 16px;
    border-bottom: 1px solid #E4E2DC;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #A8A49D;
}
.itc-folder-list { padding: 6px; }

.itc-folder-item {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 9px 10px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    font-size: 13.5px;
    font-weight: 450;
    color: #6B6860;
    user-select: none;
}
.itc-folder-item:hover {
    background: #FAFAF8;
    color: #1A1916;
}
.itc-folder-item.active {
    background: #1A1916;
    color: #ffffff;
}

.itc-folder-icon {
    width: 18px;
    height: 18px;
    object-fit: contain;
    flex-shrink: 0;
    opacity: 0.75;
}
.itc-folder-item.active .itc-folder-icon {
    opacity: 1;
    filter: brightness(0) invert(1);
}

.itc-folder-label {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.itc-folder-count {
    margin-left: auto;
    font-size: 11px;
    font-family: 'DM Mono', monospace;
    background: rgba(0,0,0,0.07);
    border-radius: 20px;
    padding: 1px 7px;
    font-weight: 500;
    flex-shrink: 0;
}
.itc-folder-item.active .itc-folder-count {
    background: rgba(255,255,255,0.18);
}

/* ── MAIN ────────────────────────────────────── */
.itc-main { display: flex; flex-direction: column; gap: 14px; }

/* ── NO SELECT ───────────────────────────────── */
.itc-no-select {
    background: #ffffff;
    border: 1.5px dashed #E4E2DC;
    border-radius: 10px;
    padding: 72px 24px;
    text-align: center;
}
.itc-no-select-icon {
    font-size: 30px;
    color: #C8C4BC;
    margin-bottom: 12px;
}
.itc-no-select p {
    font-size: 14px;
    color: #A8A49D;
    line-height: 1.6;
}

/* ── TOOLBAR ─────────────────────────────────── */
.itc-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
}
.itc-search-wrap {
    position: relative;
    flex: 1;
}
.itc-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #A8A49D;
    font-size: 12px;
    pointer-events: none;
}
.itc-search-input {
    width: 100%;
    background: #ffffff;
    border: 1px solid #E4E2DC;
    border-radius: 6px;
    padding: 9px 12px 9px 33px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    color: #1A1916;
    outline: none;
    transition: border-color 0.15s;
}
.itc-search-input:focus { border-color: #C8C4BC; }
.itc-search-input::placeholder { color: #C8C4BC; }

.itc-filter-select {
    background: #ffffff;
    border: 1px solid #E4E2DC;
    border-radius: 6px;
    padding: 9px 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    color: #6B6860;
    outline: none;
    cursor: pointer;
    flex-shrink: 0;
}
.itc-filter-select:focus { border-color: #C8C4BC; }

.itc-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: #D63B2F;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    padding: 9px 16px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
    white-space: nowrap;
    flex-shrink: 0;
}
.itc-btn-primary:hover { background: #B8302A; }
.itc-btn-primary:disabled {
    background: #E4E2DC;
    color: #A8A49D;
    cursor: not-allowed;
}

/* ── EMPTY ───────────────────────────────────── */
.itc-empty {
    background: #ffffff;
    border: 1px solid #E4E2DC;
    border-radius: 10px;
    padding: 64px 24px;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.itc-empty-icon {
    font-size: 34px;
    color: #C8C4BC;
    margin-bottom: 14px;
}
.itc-empty strong {
    display: block;
    font-size: 15px;
    font-weight: 500;
    color: #6B6860;
    margin-bottom: 6px;
}
.itc-empty p {
    font-size: 13.5px;
    color: #A8A49D;
}

/* ── TABLE ───────────────────────────────────── */
.itc-table-wrap {
    background: #ffffff;
    border: 1px solid #E4E2DC;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    animation: itcFadeIn 0.18s ease;
}
@keyframes itcFadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
}

.itc-table { width: 100%; border-collapse: collapse; }

.itc-table thead tr {
    background: #FAFAF8;
    border-bottom: 1px solid #E4E2DC;
}
.itc-table th {
    text-align: left;
    padding: 11px 16px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #A8A49D;
}

.itc-table td {
    padding: 0;
    border-bottom: 1px solid #E4E2DC;
    vertical-align: middle;
}
.itc-table tbody tr:last-child td { border-bottom: none; }

.itc-row { transition: background 0.1s; }
.itc-row:hover td { background: #FAFAF8; }

.itc-cell-input {
    width: 100%;
    display: block;
    background: transparent;
    border: none;
    padding: 13px 16px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 450;
    color: #1A1916;
    outline: none;
}
.itc-cell-input:focus {
    background: #FFFDF5;
    box-shadow: inset 0 0 0 1px #E8D88A;
}

.itc-cell-select {
    width: 100%;
    display: block;
    background: transparent;
    border: none;
    padding: 13px 16px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    color: #6B6860;
    outline: none;
    cursor: pointer;
    appearance: none;
}

.itc-duration-cell {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    padding-right: 16px;
}
.itc-duration-input {
    width: 60px;
    text-align: right;
    background: transparent;
    border: none;
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    color: #6B6860;
    outline: none;
    padding: 13px 0;
}
.itc-duration-input:focus { color: #1A1916; }
.itc-duration-unit {
    font-size: 11px;
    color: #A8A49D;
    font-family: 'DM Mono', monospace;
}

.itc-action-cell {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    padding: 0 16px;
}
.itc-btn-icon {
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px 7px;
    border-radius: 5px;
    color: #A8A49D;
    font-size: 12px;
    transition: background 0.15s, color 0.15s;
    line-height: 1;
}
.itc-btn-icon:hover { background: #F5F4F1; color: #1A1916; }
.itc-btn-icon.itc-btn-danger:hover { background: #FDF1F0; color: #D63B2F; }

/* ── MODAL ───────────────────────────────────── */
.itc-modal-inner { font-family: 'DM Sans', sans-serif; }

.itc-modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 22px 24px 18px;
    border-bottom: 1px solid #E4E2DC;
}
.itc-modal-title {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.2px;
    color: #1A1916;
    margin: 0;
}
.itc-modal-cat-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #1A1916;
    color: #ffffff;
    font-size: 11.5px;
    font-weight: 500;
    padding: 3px 10px;
    border-radius: 20px;
    margin-top: 7px;
}
.itc-modal-cat-icon {
    width: 14px;
    height: 14px;
    object-fit: contain;
    filter: brightness(0) invert(1);
}
.itc-btn-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #A8A49D;
    font-size: 15px;
    padding: 3px;
    line-height: 1;
    transition: color 0.15s;
    flex-shrink: 0;
}
.itc-btn-close:hover { color: #1A1916; }

.itc-modal-body {
    padding: 22px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.itc-form-group { display: flex; flex-direction: column; gap: 6px; }
.itc-form-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #6B6860;
}
.itc-form-input,
.itc-form-select,
.itc-form-textarea {
    background: #F5F4F1;
    border: 1px solid #E4E2DC;
    border-radius: 6px;
    padding: 10px 13px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: #1A1916;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
    width: 100%;
}
.itc-form-input:focus,
.itc-form-select:focus,
.itc-form-textarea:focus {
    border-color: #C8C4BC;
    background: #ffffff;
}
.itc-form-textarea { resize: vertical; min-height: 76px; }
.itc-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

.itc-modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #E4E2DC;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    background: #FAFAF8;
}
.itc-btn-secondary {
    background: none;
    border: 1px solid #E4E2DC;
    border-radius: 6px;
    padding: 9px 16px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 500;
    color: #6B6860;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
}
.itc-btn-secondary:hover { border-color: #C8C4BC; color: #1A1916; }
</style>